import { AdminScreenKey } from './types/admin-roles';
import { AdminFeatures } from './types/audit';

export interface NavItem {
  to: string;
  icon: string;
  label: string;
  /**
   * Spec 050 — screen key gating this entry. Omitted only for super-admin-only items. Several keys
   * mean any one of them shows the entry (#1113: Finance shows with `finance` or `finance-config`).
   */
  screen?: AdminScreenKey | AdminScreenKey[];
  superAdminOnly?: boolean;
  /** Spec 069 — shown only while this Runtime Config switch is on. */
  feature?: keyof AdminFeatures;
}

/**
 * Single source of truth for the sidebar and for picking a landing route.
 * Order matters: the first entry an admin can access becomes their home screen.
 */
export const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard', screen: 'dashboard' },
  { to: '/garages', icon: '🔧', label: 'Garages', screen: 'garages' },
  { to: '/users', icon: '👥', label: 'Users', screen: 'users' },
  { to: '/categories', icon: '📋', label: 'Service Catalog', screen: 'service-catalog' },
  { to: '/service-approvals', icon: '✅', label: 'Service Approvals', screen: 'service-approvals' },
  { to: '/lookups', icon: '📖', label: 'Lookups', screen: 'lookups' },
  { to: '/terms-documents', icon: '📄', label: 'Terms Documents', screen: 'terms-documents' },
  { to: '/translations', icon: '🌐', label: 'Translations', screen: 'translations' },
  { to: '/pickup-time-slots', icon: '🕐', label: 'Pickup Time Slots', screen: 'pickup-time-slots' },
  { to: '/dynamic-config', icon: '🎛️', label: 'Configuration', screen: 'dynamic-config' },
  { to: '/orders', icon: '📦', label: 'Orders', screen: 'orders' },
  { to: '/settlements', icon: '💰', label: 'Settlements', screen: 'settlements' },
  { to: '/finance', icon: '📈', label: 'Finance', screen: ['finance', 'finance-config'] },
  { to: '/promotions', icon: '🏷️', label: 'Promo codes', screen: 'promotions' },
  { to: '/dealers', icon: '🚘', label: 'Dealers', screen: 'dealers' },
  { to: '/change-requests', icon: '📝', label: 'Change Requests', screen: 'change-requests' },
  { to: '/reported-reviews', icon: '🚩', label: 'Reported Reviews', screen: 'reported-reviews' },
  { to: '/marketing', icon: '📣', label: 'Marketing', screen: 'marketing' },
  { to: '/audit', icon: '≣', label: 'Audit', screen: 'audit', feature: 'auditScreen' },
  { to: '/admin/roles', icon: '🔐', label: 'Admin Roles', superAdminOnly: true },
];

export function visibleNavItems(
  isSuperAdmin: boolean,
  hasScreen: (screen: string) => boolean,
  features?: Partial<AdminFeatures>,
): NavItem[] {
  return NAV_ITEMS.filter((item) => {
    if (item.feature && !features?.[item.feature]) return false;
    if (item.superAdminOnly) return isSuperAdmin;
    return item.screen ? navScreens(item).some((s) => hasScreen(s)) : true;
  });
}

/** The screen keys an entry accepts (any one of them shows it). */
export function navScreens(item: NavItem): AdminScreenKey[] {
  if (!item.screen) return [];
  return Array.isArray(item.screen) ? item.screen : [item.screen];
}

/**
 * Where to send an admin who lands on "/". Returns null when they hold nothing,
 * which the caller renders as the no-access state rather than a redirect loop.
 */
export function landingRoute(
  isSuperAdmin: boolean,
  hasScreen: (screen: string) => boolean,
  features?: Partial<AdminFeatures>,
): string | null {
  return visibleNavItems(isSuperAdmin, hasScreen, features)[0]?.to ?? null;
}
